import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";
import {
  MapContainer,
  Marker,
  TileLayer,
  GeoJSON,
  useMap,
} from "react-leaflet";
import { icon } from "leaflet";
import "leaflet/dist/leaflet.css";
import MarkerIcon from "./media/marker-icon.png";
import Cuenca from "./resources/cuenca.json";

const MapInitializer = forwardRef(function MapInitializer({ ...props }, ref) {
  const map = useMap();
  useImperativeHandle(ref, () => map, [map]);
  return;
});

const PositionMarker = forwardRef(
  ({ coordinates, zoom, onDragStart, onDragEnd, disabled, ...props }, ref) => {
    const map = useMap();
    const markerRef = useRef();
    const handlers = {
      dragstart: () => {
        const marker = markerRef.current;
        if (onDragStart instanceof Function) {
          onDragStart(marker);
        }
      },
      dragend: () => {
        const marker = markerRef.current;
        if (onDragEnd instanceof Function) {
          onDragEnd(marker);
        }
      },
    };
    useEffect(() => {
      if (!!coordinates) {
        map.flyTo(coordinates, zoom);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coordinates]);
    return (
      <Marker
        icon={icon({ iconUrl: MarkerIcon })}
        draggable={!disabled}
        ref={markerRef}
        position={coordinates}
        eventHandlers={handlers}
        {...props}
      />
    );
  },
);

export default function MapField({
  id,
  name,
  url,
  value,
  className,
  attribution,
  zoom,
  onChange,
  zoomControl,
  dragging,
  scrollWheelZoom,
  mapStyle,
  ...props
}) {
  const computedClassName = ["form-group", ...className.split(" ")]
    .join()
    .trim();
  const fieldRef = useRef();
  const mapRef = useRef();
  useLayoutEffect(() => {
    if (!!mapRef.current) {
      mapRef.current.invalidateSize({ animate: true });
    }
  });
  const handleDragEnd = (marker) => {
    const latlng = marker.getLatLng();
    if (!!fieldRef.current) {
      const field = fieldRef.current;
      if (!!latlng) {
        field.value = {
          type: "Point",
          coordinates: [latlng.lat, latlng.lng],
        };
      } else {
        field.value = null;
      }
      field.name = name;
      field.dispatchEvent(
        new Event("change", {
          bubbles: true,
          cancelable: true,
          composed: false,
        }),
      );
    }
  };
  useEffect(() => {
    const field = fieldRef.current;
    if (!!field) {
      field.addEventListener("change", onChange);
    }
    return () => {
      if (!!field) {
        field.removeEventListener("change", onChange);
      }
    };
  }, [onChange]);
  return (
    <div className={computedClassName} {...props} ref={fieldRef}>
      <MapContainer
        center={value.coordinates}
        zoom={zoom}
        zoomControl={zoomControl}
        dragging={dragging}
        scrollWheelZoom={scrollWheelZoom}
        style={mapStyle}
      >
        <MapInitializer ref={mapRef} />
        <PositionMarker
          coordinates={value.coordinates}
          zoom={18}
          onDragEnd={handleDragEnd}
        />
        <TileLayer attribution={attribution} url={url} />
        <GeoJSON data={Cuenca} />
      </MapContainer>
    </div>
  );
}

MapField.defaultProps = {
  className: "",
  attribution:
    '<a href="http://leafletjs.com" title="A JS library for interactive maps">Leaflet</a> | <a href="http://www.ign.gob.ar/AreaServicios/Argenmap/IntroduccionV2" target="_blank">Instituto Geográfico Nacional</a> + <a href="http://www.osm.org/copyright" target="_blank">OpenStreetMap</a>',
  url: "https://wms.ign.gob.ar/geoserver/gwc/service/tms/1.0.0/capabaseargenmap@EPSG%3A3857@png/{z}/{x}/{-y}.png",
  onChange: (event) => {},
};
